import {
  Button,
  Card,
  CardBody,
  CardHeader,
  type ColumnDef,
  DataTable,
  EmptyState,
  Field,
  Input,
  SegmentedButtonGroup,
  Stack,
  StatTile,
  Text,
} from '@olytic/foundation';
import { ValueLever } from '@olytic/foundation/cpq';
import { useEffect, useState } from 'react';

import { formatCurrency, formatNumber } from '../objects/format';
import { NumberSetting } from './fields';
import {
  type Assumption,
  BENEFIT_TYPES,
  impactOf,
  type Metric,
  type QualBenefit,
  SCENARIOS,
  UNITS,
  type ValueModel,
  valueTotals,
  withScenario,
} from '@quote-math/valueModel';

const newId = () => Math.random().toString(36).slice(2, 10);
const label = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);
const num = (v: string | null) => (v === null || v === '' ? 0 : Number(v));
const money = (n: number) => formatCurrency(Math.round(n));
/** Where a number came from, as the proposal builder listed it. */
const SOURCES = ['From our calls', 'Estimate', 'Benchmark'];

/** A text box saved on blur. */
function TextSetting({ label: text, value, onSave }: { label: string; value: string; onSave: (v: string) => void }): JSX.Element {
  const [draft, setDraft] = useState(value);
  useEffect(() => setDraft(value), [value]);
  return (
    <Field label={text}>
      <Input value={draft} onChange={(e) => setDraft(e.target.value)} onBlur={() => draft !== value && onSave(draft)} />
    </Field>
  );
}

/** The slider's top for a lever: its own max, else 100. */
const leverMax = (a: Assumption) => a.max ?? 100;

/**
 * Value: why the customer is doing this, in the proposal builder's layout.
 * Totals up top, enterprise value, today's numbers (metrics), each expected
 * improvement as a lever with a slider and its scenarios, and the benefits
 * we can't price. Pricing, ROI and the proposal all read from here.
 */
export function ValueTab({ model, onSave, goLiveFromTimeline = false }: { model: ValueModel; onSave: (model: ValueModel) => void; goLiveFromTimeline?: boolean }): JSX.Element {
  const totals = valueTotals(model);
  const save = (patch: Partial<ValueModel>) => onSave({ ...model, ...patch });
  const setMetric = (id: string, patch: Partial<Metric>) => save({ metrics: model.metrics.map((m) => (m.id === id ? { ...m, ...patch } : m)) });
  const setAssumption = (id: string, patch: Partial<Assumption>) =>
    save({ assumptions: model.assumptions.map((a) => (a.id === id ? { ...a, ...patch } : a)) });
  const setQual = (id: string, patch: Partial<QualBenefit>) => save({ quals: model.quals.map((q) => (q.id === id ? { ...q, ...patch } : q)) });
  const largest = totals.byDriver[0];

  const metricColumns: ColumnDef<Metric>[] = [
    { key: 'name', label: 'Metric', width: 240, get: (r) => r.name, editor: { kind: 'text', maxLength: 200 } },
    { key: 'value', label: 'Current', align: 'right', nowrap: true, get: (r) => formatNumber(r.value), editor: { kind: 'number', getValue: (r) => String(r.value) } },
    { key: 'unit', label: 'Unit', nowrap: true, get: (r) => r.unit, editor: { kind: 'select', options: UNITS.map((u) => ({ value: u, label: u })) } },
    {
      key: 'rate', label: '$ Per Unit', align: 'right', nowrap: true, get: (r) => (r.unit === '$ / yr' ? '—' : formatCurrency(r.rate)),
      editor: { kind: 'number', min: 0, getValue: (r) => String(r.rate) }, editable: (r) => r.unit !== '$ / yr',
    },
    { key: 'source', label: 'Source', nowrap: true, get: (r) => r.source, editor: { kind: 'select', options: SOURCES.map((v) => ({ value: v, label: v })) } },
    { key: 'note', label: 'Note', width: 300, get: (r) => r.note, editor: { kind: 'text', maxLength: 2000 } },
  ];
  const qualColumns: ColumnDef<QualBenefit>[] = [
    { key: 'benefit', label: 'Benefit', width: 240, get: (q) => q.benefit, editor: { kind: 'text', maxLength: 200 } },
    { key: 'detail', label: 'Why It Matters', width: 520, get: (q) => q.detail, editor: { kind: 'text', maxLength: 2000 } },
  ];

  const leverFields = (a: Assumption) => [
    [
      { key: 'metric', label: 'Metric', kind: 'select' as const, value: a.metric, options: model.metrics.map((m) => ({ value: m.id, label: m.name || 'Untitled metric' })), width: 22 },
      { key: 'change', label: 'Improve', kind: 'number' as const, value: String(a.change), suffix: '%' },
      { key: 'capture', label: 'Realized', kind: 'number' as const, value: a.capture === null ? '100' : String(a.capture), suffix: '%' },
      { key: 'lever', label: 'Slider', kind: 'select' as const, value: a.lever ?? 'change', options: [{ value: 'change', label: 'Improve %' }, { value: 'capture', label: 'Realized %' }] },
      { key: 'max', label: 'Max', kind: 'number' as const, value: String(leverMax(a)), suffix: '%' },
      { key: 'type', label: 'Type', kind: 'select' as const, value: a.type, options: BENEFIT_TYPES.map((t) => ({ value: t, label: t })) },
      { key: 'source', label: 'Source', kind: 'select' as const, value: SOURCES.includes(a.source) ? a.source : SOURCES[0]!, options: SOURCES.map((v) => ({ value: v, label: v })) },
    ],
    [
      { key: 'conservative', label: 'Conservative', kind: 'number' as const, value: String(a.scenarios.conservative) },
      { key: 'base', label: 'Base', kind: 'number' as const, value: String(a.scenarios.base) },
      { key: 'stretch', label: 'Stretch', kind: 'number' as const, value: String(a.scenarios.stretch) },
    ],
  ];

  const changeField = (a: Assumption, key: string, v: string) => {
    if (key === 'conservative' || key === 'base' || key === 'stretch') {
      setAssumption(a.id, { scenarios: { ...a.scenarios, [key]: num(v) }, ...(key === model.scenario ? { change: num(v) } : {}) });
    } else if (key === 'change') setAssumption(a.id, { change: num(v), scenarios: { ...a.scenarios, [model.scenario]: num(v) } });
    else if (key === 'capture') setAssumption(a.id, { capture: v === '' ? null : num(v) });
    else if (key === 'max') setAssumption(a.id, { max: Math.max(1, num(v)) });
    else if (key === 'lever') setAssumption(a.id, { lever: v === 'capture' ? 'capture' : 'change' });
    else setAssumption(a.id, { [key]: v } as Partial<Assumption>);
  };

  return (
    <Stack>
      <Text tone="muted">
        Why the customer is doing this. Record where they are today, what we expect the system to change, and the benefits that don&apos;t have a dollar
        figure. Pricing, ROI and the proposal all read from here.
      </Text>
      <div className="grid gap-2.5 md:grid-cols-2 lg:grid-cols-4">
        <StatTile className="rounded-[var(--radius-lg)] border border-[var(--border)] bg-[var(--surface)]" label="Annual Benefit" value={money(totals.gross)} note="Every quantified benefit at full value" />
        <StatTile className="rounded-[var(--radius-lg)] border border-[var(--border)] bg-[var(--surface)]" label="Risk-Adjusted Value" value={money(totals.adjusted)} note="Revenue and productivity counted at half" />
        <StatTile
          className="rounded-[var(--radius-lg)] border border-[var(--border)] bg-[var(--surface)]"
          label="Largest Driver"
          value={largest ? largest.label : '—'}
          note={largest && totals.gross ? `${money(largest.value)} a year · ${Math.round((largest.value / totals.gross) * 100)}% of the total` : 'Add a benefit below'}
        />
        <div className="rounded-[var(--radius-lg)] border border-[var(--border)] bg-[var(--surface)] p-4">
          <NumberSetting label="Months to Full Benefit" value={model.ramp} help="After launch, benefits ramp up over this many months" onSave={(v) => save({ ramp: v ?? 0 })} />
        </div>
      </div>

      <Card>
        <CardHeader title="Enterprise value" />
        <CardBody>
          <Text tone="muted">
            Owners and investors value lasting savings at a multiple of EBITDA. The ROI tab and the proposal show the added enterprise value at this multiple.
            Set it to 0 to hide it.
          </Text>
          <div className="mt-3 grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            <NumberSetting label="EBITDA Multiple" value={model.ebitdaMultiple || 0} onSave={(v) => save({ ebitdaMultiple: v ?? 0 })} />
            <TextSetting label="Described as" value={model.ebitdaNote} onSave={(v) => save({ ebitdaNote: v })} />
            <NumberSetting
              label="Kickoff to Launch (Months)"
              value={Math.round(model.goLiveMonths * 10) / 10}
              disabled={goLiveFromTimeline}
              {...(goLiveFromTimeline ? { help: 'From the timeline on the Terms tab.' } : {})}
              onSave={(v) => save({ goLiveMonths: v ?? 0 })}
            />
            <Field label="License Billed From">
              <SegmentedButtonGroup
                label="License Billed From"
                size="sm"
                options={[{ value: 'launch', label: 'Launch' }, { value: 'signing', label: 'Signing' }]}
                value={model.licenseFromSigning ? 'signing' : 'launch'}
                onChange={(v) => save({ licenseFromSigning: v === 'signing' })}
              />
            </Field>
          </div>
        </CardBody>
      </Card>

      <Card>
        <CardHeader
          title="Where they are today"
          actions={
            <Button variant="neutral" size="sm" onClick={() => save({ metrics: [...model.metrics, { id: newId(), name: 'New metric', value: 0, unit: '$ / yr', rate: 1, source: SOURCES[0]!, note: '' }] })}>
              Add metric
            </Button>
          }
        />
        <CardBody>
          <Text tone="muted">The current numbers the system should move. Use dollars where you can; hours and counts take a dollar value per unit.</Text>
        </CardBody>
        {model.metrics.length ? (
          <DataTable<Metric>
            columns={metricColumns}
            rows={model.metrics}
            getRowId={(r) => r.id}
            density="compact"
            onCellSave={(r, key, v) => setMetric(r.id, key === 'value' || key === 'rate' ? { [key]: num(v) } : { [key]: v ?? '' })}
            rowActions={[{ label: 'Remove', destructive: true, onSelect: (r) => save({ metrics: model.metrics.filter((m) => m.id !== r.id) }) }]}
          />
        ) : null}
      </Card>

      <Card>
        <CardHeader
          title="How much improvement to expect"
          actions={
            <SegmentedButtonGroup
              label="Scenario"
              size="sm"
              options={SCENARIOS.map((s) => ({ value: s, label: label(s) }))}
              value={model.scenario}
              onChange={(v) => onSave(withScenario(model, v))}
            />
          }
        />
        <CardBody>
          <Text tone="muted">
            Pick a starting point, then adjust any lever. Realized is the share of an improvement that turns into money, such as freed hours filled with billable work.
          </Text>
        </CardBody>
        {model.assumptions.length ? (
          <div className="border-t border-[var(--border)]">
            {model.assumptions.map((a) => {
              const onCapture = a.lever === 'capture';
              return (
                <ValueLever
                  key={a.id}
                  title={a.label}
                  note={a.note}
                  value={onCapture ? (a.capture ?? 100) : a.change}
                  max={leverMax(a)}
                  impact={money(impactOf(a, model.metrics))}
                  fields={leverFields(a)}
                  onValueChange={(v) => changeField(a, onCapture ? 'capture' : 'change', String(v))}
                  onTitleChange={(v) => setAssumption(a.id, { label: v })}
                  onNoteChange={(v) => setAssumption(a.id, { note: v })}
                  onFieldChange={(key, v) => changeField(a, key, v)}
                  onRemove={() => save({ assumptions: model.assumptions.filter((x) => x.id !== a.id) })}
                />
              );
            })}
          </div>
        ) : null}
        <CardBody>
          <Button
            variant="neutral"
            size="sm"
            disabled={model.metrics.length === 0}
            onClick={() =>
              save({
                assumptions: [
                  ...model.assumptions,
                  { id: newId(), metric: model.metrics[0]?.id ?? '', label: 'New benefit', change: 10, capture: null, type: 'Cost savings', source: SOURCES[0]!, note: '', scenarios: { conservative: 5, base: 10, stretch: 15 }, lever: 'change', max: 25 },
                ],
              })
            }
          >
            Add assumption
          </Button>
        </CardBody>
      </Card>

      <Card>
        <CardHeader
          title="Benefits we can't price"
          actions={
            <Button variant="neutral" size="sm" onClick={() => save({ quals: [...model.quals, { id: newId(), benefit: 'New benefit', detail: '' }] })}>
              Add
            </Button>
          }
        />
        <CardBody>
          <Text tone="muted">Qualitative wins worth naming in the proposal.</Text>
        </CardBody>
        {model.quals.length ? (
          <DataTable<QualBenefit>
            columns={qualColumns}
            rows={model.quals}
            getRowId={(r) => r.id}
            density="compact"
            onCellSave={(q, key, v) => setQual(q.id, { [key]: v ?? '' })}
            rowActions={[{ label: 'Remove', destructive: true, onSelect: (r) => save({ quals: model.quals.filter((q) => q.id !== r.id) }) }]}
          />
        ) : (
          <CardBody>
            <EmptyState title="None yet" description="Gains that matter but have no dollar figure, such as growing without hiring." />
          </CardBody>
        )}
      </Card>
    </Stack>
  );
}
